"use client";

import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { useBookingDrawer } from "@/lib/booking-context";
import { useCallbackPanel } from "@/lib/callback-context";
import { useFaqDrawer } from "@/lib/faq-context";
import { useMenuDrawer } from "@/lib/menu-context";
import { useQuiz } from "@/lib/quiz-context";
import { useSearch } from "@/lib/search-context";

import type { NavSettings } from "@/lib/nav";
import type { SearchEntry } from "@/lib/search";

import type { NavItem } from "@/components/optilase/header-view";
import type { QuizPrices } from "@/lib/quiz-flow";

const loadFaqDrawer = () => import("@/components/faq/faq-drawer");
const FaqDrawer = dynamic(() => loadFaqDrawer().then((m) => m.FaqDrawer), { ssr: false });
const loadBookingDrawer = () => import("@/components/layout/booking-drawer");
const BookingDrawer = dynamic(() => loadBookingDrawer().then((m) => m.BookingDrawer), {
  ssr: false,
});
const loadMenuDialog = () => import("@/components/layout/menu-dialog");
const MenuDialog = dynamic(() => loadMenuDialog().then((m) => m.MenuDialog), { ssr: false });
const loadMenuDrawer = () => import("@/components/layout/menu-drawer");
const MenuDrawer = dynamic(() => loadMenuDrawer().then((m) => m.MenuDrawer), { ssr: false });
const loadCallbackPanel = () => import("@/components/layout/callback-panel");
const CallbackPanel = dynamic(() => loadCallbackPanel().then((m) => m.CallbackPanel), {
  ssr: false,
});
const loadQuizDialog = () => import("@/components/quiz/quiz-dialog");
const QuizDialog = dynamic(() => loadQuizDialog().then((m) => m.QuizDialog), { ssr: false });
const loadSearchDialog = () => import("@/components/search/search-dialog");
const SearchDialog = dynamic(() => loadSearchDialog().then((m) => m.SearchDialog), { ssr: false });

const INTERACTIONS = ["pointerdown", "pointermove", "keydown", "touchstart", "scroll"] as const;

export function WarmModals() {
  useEffect(() => {
    const warm = () => {
      INTERACTIONS.forEach((event) => window.removeEventListener(event, warm));
      void loadFaqDrawer();
      void loadBookingDrawer();
      void loadMenuDialog();
      void loadMenuDrawer();
      void loadCallbackPanel();
      void loadQuizDialog();
      void loadSearchDialog();
    };
    INTERACTIONS.forEach((event) =>
      window.addEventListener(event, warm, { once: true, passive: true }),
    );
    return () => INTERACTIONS.forEach((event) => window.removeEventListener(event, warm));
  }, []);
  return null;
}

function useOpenedOnce(open: boolean): boolean {
  const [opened, setOpened] = useState(open);
  if (open && !opened) setOpened(true);
  return opened;
}

export function LazyFaqDrawer() {
  const { open } = useFaqDrawer();
  return useOpenedOnce(open) ? <FaqDrawer /> : null;
}

export function LazyCallbackPanel() {
  const { open } = useCallbackPanel();
  return useOpenedOnce(open) ? <CallbackPanel /> : null;
}

export function LazyQuizDialog({
  points,
  prices,
  quizImage,
  quizImageFocus,
}: {
  prices?: QuizPrices;
  quizImage?: string | null;
  quizImageFocus?: string | null;
  points?: string[];
}) {
  const { open } = useQuiz();
  const deepLink = useSearchParams().get("quiz") === "open";
  if (!useOpenedOnce(open || deepLink)) return null;
  return (
    <QuizDialog
      prices={prices}
      quizImage={quizImage}
      quizImageFocus={quizImageFocus}
      points={points}
    />
  );
}

export function LazyBookingDrawer({
  bookingImage,
  clinics,
}: {
  bookingImage?: string | null;
  clinics?: { label: string; value: string }[];
}) {
  const { open } = useBookingDrawer();
  if (!useOpenedOnce(open)) return null;
  return <BookingDrawer bookingImage={bookingImage} clinics={clinics} />;
}

export function LazyMenuDialog({
  navSettings,
  navItems,
}: {
  navSettings: NavSettings | null;
  navItems?: NavItem[];
}) {
  const { open } = useMenuDrawer();
  if (!useOpenedOnce(open)) return null;
  return <MenuDialog navSettings={navSettings} navItems={navItems} />;
}

export function LazyMenuDrawer({
  navSettings,
  navItems,
}: {
  navSettings: NavSettings | null;
  navItems?: NavItem[];
}) {
  const { open } = useMenuDrawer();
  if (!useOpenedOnce(open)) return null;
  return <MenuDrawer navSettings={navSettings} navItems={navItems} />;
}

export function LazySearchDialog({
  navSettings,
  navItems,
  index,
}: {
  navSettings: NavSettings | null;
  navItems?: NavItem[];
  index?: SearchEntry[];
}) {
  const { open } = useSearch();
  if (!useOpenedOnce(open)) return null;
  return <SearchDialog navSettings={navSettings} navItems={navItems} index={index} />;
}
